'use client';

import { InventoryTypeBadge } from '@/components/Inventory/Badges/InventoryTypeBadge';
import { PromotionsBadges } from '@/components/Inventory/Badges/PromotionsBadges';
import { InventoryPricing } from '@/components/Inventory/InventoryPricing';
import { getUnlockPriceLabel } from '@/tools/inventory';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getFacetCode } from '@lamesarv-sdk/tools/dist/inventory';
import { IInventory, InventoryField } from '@lamesarv-sdk/types';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface DesktopCtaProps {
  item: IInventory;
  allowedSimilarMakeModelSearch?: boolean;
}

export const DesktopCta = ({ item, allowedSimilarMakeModelSearch }: DesktopCtaProps) => {
  const [showBar, setShowBar] = useState(false);
  const targetRef = useRef<HTMLAnchorElement>(null);
  const similarRVsUrl = allowedSimilarMakeModelSearch
    ? `/search?make=${getFacetCode(item[InventoryField.make])}&model=${getFacetCode(item[InventoryField.model])}&ref=sold_similar`
    : `/search?body=${getFacetCode(item[InventoryField.body])}&ref=sold_similar`;

  useEffect(() => {
    const handleScroll = () => {
      if (targetRef.current) {
        const rect = targetRef.current.getBoundingClientRect();

        setShowBar(rect.bottom <= 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <a
        href={item[InventoryField.isSold] ? similarRVsUrl : '#pdpform'}
        ref={targetRef}
        className={twMerge(
          item[InventoryField.isSold] && 'sold-shop-similar',
          'rounded bg-blue-500 text-white font-bold py-3 flex items-center justify-center max-lg:hidden mt-6 hover:bg-blue-600',
        )}
        data-tag="cta-button-pdp"
        data-testid="rvunit-cta"
      >
        {getUnlockPriceLabel(item).buttonLabel}
      </a>
      <div
        className={twMerge(
          'max-lg:hidden border-b border-black-200 bg-white fixed left-0 top-0 z-40 py-4 transition w-screen',
          showBar ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      >
        <div className="relative container mx-auto flex justify-between items-start px-4">
          <div className="flex flex-col gap-1.5">
            <div className="flex gap-2.5">
              <InventoryTypeBadge inventoryType={item[InventoryField.inventoryType]} />
              {!item[InventoryField.isSold] && <PromotionsBadges tags={item[InventoryField.customTags]} />}
            </div>
            <h2 className="text-black-500 text-xl font-bold">{item[InventoryField.title]}</h2>
            <div className="flex items-center gap-3 text-black-350 text-sm font-medium">
              <span>Stock# {item[InventoryField.stockNumber]}</span>
              {item[InventoryField.vinFull] && <span>VIN: {item[InventoryField.vinFull]}</span>}
              <div className="flex gap-0.5 items-center">
                <FontAwesomeIcon icon={faLocationDot} className="size-4" />
                <span>{item[InventoryField.location]}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-10">
            {!item[InventoryField.isSold] && (
              <InventoryPricing smaller item={item} usePDPRebateVariant useStickyMode={showBar} />
            )}
            <figure className="border-l-2 border-black-200 self-stretch my-1" />
            <a
              href={item[InventoryField.isSold] ? similarRVsUrl : '#pdpform'}
              className={twMerge(
                item[InventoryField.isSold] && 'sold-shop-similar',
                'rounded bg-blue-500 text-white font-bold py-3 flex items-center justify-center max-lg:hidden px-12 hover:bg-blue-600',
              )}
              data-tag="cta-button-pdp-fixed-header"
              data-testid="rvunit-cta-fixed"
            >
              {getUnlockPriceLabel(item).buttonLabel}
            </a>
          </div>
        </div>
      </div>
    </>
  );
};
